// src/hooks/useProductMode.js
//
// Central hook for auth-aware product mode.
// Consumed by TriagePage, TriageResults, CalculatorPage, CalculatorSavedPage,
// and now StartPage (portal front door, '/').
//
// Never invent if (user) branching for UX routing in individual pages —
// use this hook so all tools stay in sync.
//
// mode: 'authenticated' | 'anonymous'
// ownershipState({ is_owner, is_claimed }): 'owned' | 'unclaimed' | 'public'
// otherProductDone(currentProduct): bool — has this user completed the OTHER
//   free product (existence check only, no tax_year join — see note below)
// productSummaries: richer per-product state for the start page (see below)

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { useSubjects } from './useSubjects'

const EMPTY_SUMMARIES = {
  triage: { status: 'none', publicToken: null },
  calculator: {
    status: 'none', publicToken: null, sessionId: null, taxYear: null,
    isAutonomo: false, irpfGross: null, estimateLow: null, estimateHigh: null,
    createdAt: null,
  },
}

export function useProductMode() {
  const { user, loading: authLoading }   = useAuth()
  const { activeSubject, activeTaxYear } = useSubjects()

  const isAuthenticated = !!user
  const mode            = isAuthenticated ? 'authenticated' : 'anonymous'
  const activeSubjectId = activeSubject?.id || null

  // ── Cross-product completion (Phase 4) + start-page summaries ─────────────
  // Deliberately owner_user_id-only, no tax_year matching. tax_year is a
  // filing-year concept (deadline-anchored, triage/assessment concern) — it
  // has no bearing on "has this user ever completed the other product."
  // Existence is existence, regardless of which year it was for.
  //
  // productsDone (boolean pair, pre-existing) and productSummaries (richer,
  // added for StartPage.js) are both DERIVED from the same one set of
  // queries below — not two separate fetches. productsDone stays exactly
  // the shape every existing caller (otherProductDone) already depends on;
  // productSummaries is purely additive.
  const [productsDone, setProductsDone]         = useState({ calculator: false, triage: false })
  const [productSummaries, setProductSummaries] = useState(EMPTY_SUMMARIES)

  const fetchProductState = useCallback(async () => {
    if (!user) {
      setProductsDone({ calculator: false, triage: false })
      setProductSummaries(EMPTY_SUMMARIES)
      return null
    }

    const [triageRes, calcPublishedRes] = await Promise.all([
      supabase.from('triage_sessions')
        .select('public_token')
        .eq('owner_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      // Most-recent PUBLISHED, non-deleted session — same filter/order
      // pattern as IntelligencePage.js's main card query and
      // generate-case-brief/get-advisor-pipeline's "most recent published"
      // convention (is_representative retirement, see
      // advisor_dashboard_case_brief_framework.md). A subject can have
      // several published sessions; this is the default-view convention,
      // not a claim that it's uniquely "the" session.
      supabase.from('calculator_sessions')
        .select('public_token, irpf_gross, estimate_low, estimate_high, tax_year, is_autonomo')
        .eq('owner_user_id', user.id)
        .not('published_at', 'is', null)
        .is('deleted_at', null)
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ])

    // Only look for a draft if there's no published session — a published
    // session already means "done" for this card; a separate newer draft
    // is workspace-level nuance (IntelligencePage.js's own scenario list),
    // not something the compact start-page card needs to reason about.
    let calcDraftRes = { data: null }
    if (!calcPublishedRes.data) {
      calcDraftRes = await supabase.from('calculator_sessions')
        .select('id, tax_year, is_autonomo, created_at')
        .eq('owner_user_id', user.id)
        .is('published_at', null)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
    }

    const triageDone = !!triageRes.data
    const calc  = calcPublishedRes.data
    const draft = calcDraftRes.data

    const summaries = {
      triage: triageDone
        ? { status: 'done', publicToken: triageRes.data.public_token }
        : { status: 'none', publicToken: null },
      calculator: calc
        ? {
            status: 'done', publicToken: calc.public_token, sessionId: null,
            taxYear: calc.tax_year, isAutonomo: !!calc.is_autonomo,
            irpfGross: calc.irpf_gross, estimateLow: calc.estimate_low,
            estimateHigh: calc.estimate_high, createdAt: null,
          }
        : draft
        ? {
            status: 'draft', publicToken: null, sessionId: draft.id,
            taxYear: draft.tax_year, isAutonomo: !!draft.is_autonomo,
            irpfGross: null, estimateLow: null, estimateHigh: null,
            createdAt: draft.created_at,
          }
        : {
            status: 'none', publicToken: null, sessionId: null, taxYear: null,
            isAutonomo: false, irpfGross: null, estimateLow: null,
            estimateHigh: null, createdAt: null,
          },
    }

    setProductsDone({ calculator: summaries.calculator.status !== 'none', triage: triageDone })
    setProductSummaries(summaries)
    return summaries
  }, [user])

  useEffect(() => {
    if (!user) {
      setProductsDone({ calculator: false, triage: false })
      setProductSummaries(EMPTY_SUMMARIES)
      return
    }
    let cancelled = false

    fetchProductState().then((result) => {
      if (cancelled || !result) return
      // If this is a freshly authenticated session and neither product
      // shows as done yet, a claim-session write (triggered from
      // ResultCTA, typically on a different page/mount than wherever this
      // hook happens to be running — e.g. this page was reached by
      // navigating away from the result page after claiming) may simply
      // not have landed in the DB yet by the time this query fired. There's
      // no signal connecting the two mounts to know when it's safe to
      // check. One retry after a short delay covers that race without
      // adding any cross-page wiring. If both are still 'none' after the
      // retry, that's the real state (a genuinely new user), not a bug —
      // no further retries.
      if (result.triage.status === 'none' && result.calculator.status === 'none') {
        setTimeout(() => {
          if (!cancelled) fetchProductState()
        }, 1500)
      }
    })

    return () => { cancelled = true }
  }, [user, fetchProductState])

  // currentProduct: 'calculator' | 'triage' — returns whether the OTHER one is done
  function otherProductDone(currentProduct) {
    if (!isAuthenticated) return false
    return currentProduct === 'calculator' ? productsDone.triage : productsDone.calculator
  }

  // Determine ownership of a result row.
  // flags: { is_owner, is_claimed } as returned by the token-read RPCs
  // (src/lib/sessionByToken.js); the owner's real id never reaches the client.
  function ownershipState({ is_owner, is_claimed } = {}) {
    if (!isAuthenticated)  return 'public'
    if (is_owner)          return 'owned'
    if (!is_claimed)       return 'unclaimed'
    return 'public'   // claimed by a different user
  }

  return {
    isAuthenticated,
    mode,                  // 'authenticated' | 'anonymous'
    user,
    authLoading,           // true until AuthContext's initial getSession() resolves —
                           // consumers with a "run once on mount" init effect (e.g.
                           // CalculatorSavedPage.js, TriageResults.js) should gate on
                           // this instead of firing immediately on mount, or they'll
                           // capture a stale, pre-resolution `user` (always null) and
                           // never re-evaluate.
    activeSubjectId,
    activeTaxYear,
    ownershipState,
    otherProductDone,
    // productSummaries: { triage: {...}, calculator: {...} } — see
    // EMPTY_SUMMARIES above for the full shape. For an anonymous visitor
    // this is always EMPTY_SUMMARIES ('none' for both) — by design, not a
    // gap: there is currently no account-creation path other than
    // completing a product first, so "anonymous" and "has no done state"
    // are the same population. See portal_start_page_plan.md.
    productSummaries,
    // Exposed for callers that know a claim just happened on THIS same
    // mount (e.g. ResultCTA, right after claimSession() resolves) and want
    // an immediate re-check rather than waiting on the retry above.
    refetchProductsDone: fetchProductState,
    hubCTA: isAuthenticated
      ? { label: 'Go to Intelligence Hub →', href: '/intelligence' }
      : null,
  }
}